import { QuestionnaireResponseItem } from '@medplum/fhirtypes'
import { SupervisoryReviewFormField } from './formField'
import { RocAssessmentQuestionnaireItem } from '../../types/questionnaire'
import { FlatAnswerMutation, SectionAdapter } from '../../../adapters/types'
import { AdapterOptions } from '../../../adapters/types'
import { getAnswerValuesFromItems } from '../../../helpers'
import { RocFormData } from '../../types/RocFormData'

/**
 * Supervisory Review subsection adapter
 */
export class SupervisoryReviewAdapter extends SectionAdapter<RocFormData> {
  extractFormData(
    flatAnswers: Record<string, QuestionnaireResponseItem>,
    _options?: AdapterOptions
  ): Partial<RocFormData> {
    const flatAnswersArray = Object.values(flatAnswers)

    const idBadge = getAnswerValuesFromItems(
      flatAnswersArray,
      RocAssessmentQuestionnaireItem.SUPERVISORY_REVIEW_ID_BADGE
    ).at(0)

    const honoringRights = getAnswerValuesFromItems(
      flatAnswersArray,
      RocAssessmentQuestionnaireItem.SUPERVISORY_REVIEW_HONORING_RIGHTS
    ).at(0)

    const emergencyProtocol = getAnswerValuesFromItems(
      flatAnswersArray,
      RocAssessmentQuestionnaireItem.SUPERVISORY_REVIEW_EMERGENCY_PROTOCOL
    ).at(0)

    const hygienePrecautions = getAnswerValuesFromItems(
      flatAnswersArray,
      RocAssessmentQuestionnaireItem.SUPERVISORY_REVIEW_HYGIENE_PRECAUTIONS
    ).at(0)

    const reportingChanges = getAnswerValuesFromItems(
      flatAnswersArray,
      RocAssessmentQuestionnaireItem.SUPERVISORY_REVIEW_REPORTING_CHANGES
    ).at(0)

    const grievanceComplaint = getAnswerValuesFromItems(
      flatAnswersArray,
      RocAssessmentQuestionnaireItem.SUPERVISORY_REVIEW_GRIEVANCE_COMPLAINT
    ).at(0)

    const pocDocReviewedWithCaregiver = getAnswerValuesFromItems(
      flatAnswersArray,
      RocAssessmentQuestionnaireItem.SUPERVISORY_REVIEW_POC_DOC_REVIEWED_WITH_CAREGIVER
    ).at(0)

    const ppeAccess = getAnswerValuesFromItems(
      flatAnswersArray,
      RocAssessmentQuestionnaireItem.SUPERVISORY_REVIEW_PPE_ACCESS
    ).at(0)

    const supervisionObserved = getAnswerValuesFromItems(
      flatAnswersArray,
      RocAssessmentQuestionnaireItem.SUPERVISORY_REVIEW_SUPERVISION_OBSERVED
    ).at(0)

    const taskObserved = getAnswerValuesFromItems(
      flatAnswersArray,
      RocAssessmentQuestionnaireItem.SUPERVISORY_REVIEW_TASK_OBSERVED
    ).at(0)

    const patientStatus = getAnswerValuesFromItems(
      flatAnswersArray,
      RocAssessmentQuestionnaireItem.SUPERVISORY_REVIEW_PATIENT_STATUS
    ).at(0)

    const comments = getAnswerValuesFromItems(
      flatAnswersArray,
      RocAssessmentQuestionnaireItem.SUPERVISORY_REVIEW_COMMENTS
    ).at(0)

    const caregiverEducationTopic = getAnswerValuesFromItems(
      flatAnswersArray,
      RocAssessmentQuestionnaireItem.SUPERVISORY_REVIEW_EDUCATION_CAREGIVER_EDUCATION_TOPIC
    ).at(0)

    const educationProvided = getAnswerValuesFromItems(
      flatAnswersArray,
      RocAssessmentQuestionnaireItem.SUPERVISORY_REVIEW_EDUCATION_EDUCATION_PROVIDED
    ).at(0)

    return {
      [SupervisoryReviewFormField.ID_BADGE]: idBadge,
      [SupervisoryReviewFormField.HONORING_RIGHTS]: honoringRights,
      [SupervisoryReviewFormField.EMERGENCY_PROTOCOL]: emergencyProtocol,
      [SupervisoryReviewFormField.HYGIENE_PRECAUTIONS]: hygienePrecautions,
      [SupervisoryReviewFormField.REPORTING_CHANGES]: reportingChanges,
      [SupervisoryReviewFormField.GRIEVANCE_COMPLAINT]: grievanceComplaint,
      [SupervisoryReviewFormField.POC_DOC_REVIEWED_WITH_CAREGIVER]: pocDocReviewedWithCaregiver,
      [SupervisoryReviewFormField.PPE_ACCESS]: ppeAccess,
      [SupervisoryReviewFormField.SUPERVISION_OBSERVED]: supervisionObserved,
      [SupervisoryReviewFormField.TASK_OBSERVED]: taskObserved,
      [SupervisoryReviewFormField.PATIENT_STATUS]: patientStatus,
      [SupervisoryReviewFormField.COMMENTS]: comments,
      [SupervisoryReviewFormField.CAREGIVER_EDUCATION_TOPIC]: caregiverEducationTopic,
      [SupervisoryReviewFormField.EDUCATION_PROVIDED]: educationProvided
    }
  }

  buildFlatAnswers(formData: RocFormData): FlatAnswerMutation[] {
    return [
      {
        linkId: RocAssessmentQuestionnaireItem.SUPERVISORY_REVIEW_ID_BADGE,
        value: formData[SupervisoryReviewFormField.ID_BADGE]
      },
      {
        linkId: RocAssessmentQuestionnaireItem.SUPERVISORY_REVIEW_HONORING_RIGHTS,
        value: formData[SupervisoryReviewFormField.HONORING_RIGHTS]
      },
      {
        linkId: RocAssessmentQuestionnaireItem.SUPERVISORY_REVIEW_EMERGENCY_PROTOCOL,
        value: formData[SupervisoryReviewFormField.EMERGENCY_PROTOCOL]
      },
      {
        linkId: RocAssessmentQuestionnaireItem.SUPERVISORY_REVIEW_HYGIENE_PRECAUTIONS,
        value: formData[SupervisoryReviewFormField.HYGIENE_PRECAUTIONS]
      },
      {
        linkId: RocAssessmentQuestionnaireItem.SUPERVISORY_REVIEW_REPORTING_CHANGES,
        value: formData[SupervisoryReviewFormField.REPORTING_CHANGES]
      },
      {
        linkId: RocAssessmentQuestionnaireItem.SUPERVISORY_REVIEW_GRIEVANCE_COMPLAINT,
        value: formData[SupervisoryReviewFormField.GRIEVANCE_COMPLAINT]
      },
      {
        linkId: RocAssessmentQuestionnaireItem.SUPERVISORY_REVIEW_POC_DOC_REVIEWED_WITH_CAREGIVER,
        value: formData[SupervisoryReviewFormField.POC_DOC_REVIEWED_WITH_CAREGIVER]
      },
      {
        linkId: RocAssessmentQuestionnaireItem.SUPERVISORY_REVIEW_PPE_ACCESS,
        value: formData[SupervisoryReviewFormField.PPE_ACCESS]
      },
      {
        linkId: RocAssessmentQuestionnaireItem.SUPERVISORY_REVIEW_SUPERVISION_OBSERVED,
        value: formData[SupervisoryReviewFormField.SUPERVISION_OBSERVED]
      },
      {
        linkId: RocAssessmentQuestionnaireItem.SUPERVISORY_REVIEW_TASK_OBSERVED,
        value: formData[SupervisoryReviewFormField.TASK_OBSERVED]
      },
      {
        linkId: RocAssessmentQuestionnaireItem.SUPERVISORY_REVIEW_PATIENT_STATUS,
        value: formData[SupervisoryReviewFormField.PATIENT_STATUS]
      },
      {
        linkId: RocAssessmentQuestionnaireItem.SUPERVISORY_REVIEW_COMMENTS,
        value: formData[SupervisoryReviewFormField.COMMENTS]
      },
      {
        linkId:
          RocAssessmentQuestionnaireItem.SUPERVISORY_REVIEW_EDUCATION_CAREGIVER_EDUCATION_TOPIC,
        value: formData[SupervisoryReviewFormField.CAREGIVER_EDUCATION_TOPIC]
      },
      {
        linkId: RocAssessmentQuestionnaireItem.SUPERVISORY_REVIEW_EDUCATION_EDUCATION_PROVIDED,
        value: formData[SupervisoryReviewFormField.EDUCATION_PROVIDED]
      }
    ]
  }
}
